import { useState, FormEvent, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { isValidEmail, validatePassword, getPasswordStrengthColor } from '@/utils/validation';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { devLog } from '@/utils/logger';

/**
 * Register Page
 *
 * User registration form with Agile Assets branding
 */

export const Register = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { register, isAuthenticated, isLoading: authLoading, user } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    teamName: '',
    /** Honeypot — intentionally blank; bots auto-fill it */
    website: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Capture utm_source once at mount from the URL that brought the user here.
  // useRef (not state) so a later React-Router URL change doesn't lose the value.
  const utmSourceRef = useRef<string | undefined>(
    new URLSearchParams(window.location.search).get('utm_source') ?? undefined,
  );

  const passwordValidation = validatePassword(formData.password);

  // Guard against the auto-reload wiping typed signup input mid-session.
  // Honeypot field intentionally excluded — it's never filled by real users.
  useUnsavedGuard(
    formData.email.trim().length > 0 ||
      formData.password.length > 0 ||
      formData.confirmPassword.length > 0 ||
      formData.fullName.trim().length > 0 ||
      formData.teamName.trim().length > 0,
    'register',
  );

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(user?.needsPlan ? '/shop' : '/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, user?.needsPlan]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!formData.email || !formData.password || !formData.fullName || !formData.teamName) {
      setError(t('auth_register_error_all_fields'));
      return;
    }

    if (formData.teamName.trim().length < 2) {
      setError(t('auth_register_error_brand_name_too_short'));
      return;
    }

    if (!isValidEmail(formData.email)) {
      setError(t('auth_register_error_invalid_email'));
      return;
    }

    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth_register_error_passwords_mismatch'));
      return;
    }

    setIsLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
        teamName: formData.teamName,
        website: formData.website, // Honeypot — empty for real users
        ...(utmSourceRef.current !== undefined && { utmSource: utmSourceRef.current }),
      });
      navigate('/shop', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth_register_error_generic'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      {/* Register card */}
      <div className="relative bg-white rounded-lg shadow-2xl px-7 pt-6 pb-3 sm:px-8 sm:pt-8 sm:pb-4 w-full max-w-lg flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[35%] sm:max-w-[45%] h-auto"
            onError={(e) => {
              devLog.log('Logo failed to load from:', e.currentTarget.src);
              e.currentTarget.onerror = null;
            }}
            onLoad={(e) => {
              devLog.log('Logo loaded successfully from:', e.currentTarget.src);
            }}
          />
        </div>

        {/* Tagline — desktop only */}
        <div className="hidden sm:block text-center mb-4 sm:mb-6">
          <p className="text-[11px] uppercase tracking-wider text-gray-900 leading-relaxed font-medium">
            {t('auth_register_tagline_creating')} <span className="text-bs-secondary font-bold">{t('auth_register_tagline_brand_value')}</span>.
            <br />
            {t('auth_register_tagline_one_asset')}
            <br />
            {t('auth_register_tagline_content_marketing')} <span className="text-bs-secondary font-bold">{t('auth_register_tagline_next_level')}</span>.
          </p>
        </div>

        {/* Welcome heading */}
        <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 text-center mb-5 sm:mb-8 font-wagner">
          {t('auth_register_heading')}
        </h1>

        {/* Error message */}
        {error && (
          <div
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Register form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot: hidden from humans, bots auto-fill it. Backend rejects non-empty value. */}
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          <Input
            inputSize="sm"
            labelVisible
            id="fullName"
            name="fullName"
            type="text"
            value={formData.fullName}
            onChange={handleChange}
            placeholder={t('auth_register_placeholder_fullname')}
            disabled={isLoading}
            autoComplete="name"
            required
            label={t('auth_register_label_fullname')}
          />

          <Input
            inputSize="sm"
            labelVisible
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('auth_register_placeholder_email')}
            disabled={isLoading}
            autoComplete="email"
            required
            label={t('auth_register_label_email')}
          />

          <Input
            inputSize="sm"
            labelVisible
            id="teamName"
            name="teamName"
            type="text"
            value={formData.teamName}
            onChange={handleChange}
            placeholder={t('auth_register_placeholder_team_name')}
            disabled={isLoading}
            autoComplete="organization"
            required
            label={t('auth_register_label_team_name')}
          />

          <div>
            <div className="relative">
              <Input
                inputSize="sm"
                labelVisible
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth_register_placeholder_password')}
                disabled={isLoading}
                autoComplete="new-password"
                required
                label={t('auth_register_label_password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 bottom-0 h-[34px] lg:h-11 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <span className="material-symbols-outlined !text-[18px]">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
            {formData.password && (
              <div className="mt-2">
                <div className="h-1 w-full bg-gray-200 rounded overflow-hidden">
                  <div
                    className="h-full transition-all duration-300"
                    style={{
                      width: passwordValidation.isValid
                        ? passwordValidation.strength === 'strong'
                          ? '100%'
                          : '66%'
                        : '33%',
                      backgroundColor: getPasswordStrengthColor(passwordValidation.strength),
                    }}
                  />
                </div>
                <span
                  className="text-xs mt-1 block"
                  style={{ color: getPasswordStrengthColor(passwordValidation.strength) }}
                >
                  {passwordValidation.strength.charAt(0).toUpperCase() + passwordValidation.strength.slice(1)}{t('auth_register_password_strength_suffix')}
                </span>
              </div>
            )}
            {formData.password && passwordValidation.errors.length > 0 && (
              <ul className="mt-2 text-xs text-red-600 space-y-1">
                {passwordValidation.errors.map((err, index) => (
                  <li key={index}>• {err}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="relative">
            <Input
              inputSize="sm"
              labelVisible
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('auth_register_placeholder_confirm_password')}
              disabled={isLoading}
              autoComplete="new-password"
              required
              label={t('auth_register_label_confirm_password')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 bottom-0 h-[34px] lg:h-11 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <span className="material-symbols-outlined !text-[18px]">
                {showConfirmPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>

          <div className="text-center">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              disabled={!passwordValidation.isValid}
              className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
            >
              {t('auth_register_button_create')}
            </Button>
          </div>

        </form>

        {/* Links */}
        <div className="flex items-center justify-center mt-5 sm:mt-8 mb-3">
          <Link
            to="/login"
            className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
          >
            {t('auth_register_already_have_account')} <span className="text-bs-secondary font-semibold">{t('auth_register_sign_in')}</span>
          </Link>
        </div>

        {/* Footer — stuck to bottom of card */}
        {/* eslint-disable i18next/no-literal-string */}
        <div className="mt-auto border-t border-gray-200" />
        <p className="pt-3 text-[10px] sm:text-xs text-gray-400 text-center">
          © {new Date().getFullYear()} Made with <span className="text-bs-secondary">❤️</span> by{' '}
          <a href="https://overw8.de" target="_blank" rel="noopener noreferrer" className="text-bs-secondary hover:text-secondary-600 font-medium transition-colors">OVERW8</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/house-rules/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Terms</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/packages-pricing/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Pricing</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/about/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">About</a>
        </p>
        {/* eslint-enable i18next/no-literal-string */}
      </div>
    </div>
  );
};
